import { PcbNodeTypes, TraceShapeType, } from "@buildwithflux/models";
import { Vector3 } from "three";
import { vec2 } from "../../common";
import { Arrays } from "../../helpers";
export const SegmentLength = 0.00006; // 60micrometers is deemed an acceptable margin of error, that sufficiently approximates curvature with minimal disruption to antenna/other RF requirements. We might have room to relax this to a larger value if performance becomes an issue. Dacre and Kerry picked this value 21 March 2025
export const ToleranceCornerRadius = 0.0000001;
// TODO: move this to math?
export function isCloseTo(numberA, numberB, tolerance) {
    const ratio = numberA / numberB;
    return Math.abs(1 - ratio) < tolerance;
}
function shapeBounds(shape) {
    const startX = shape.centerX + Math.cos(shape.startAngle) * shape.radius;
    const startY = shape.centerY + Math.sin(shape.startAngle) * shape.radius;
    const endX = shape.centerX + Math.cos(shape.endAngle) * shape.radius;
    const endY = shape.centerY + Math.sin(shape.endAngle) * shape.radius;
    let maxY = Math.max(startY, endY);
    let minY = Math.min(startY, endY);
    let maxX = Math.max(startX, endX);
    let minX = Math.min(startX, endX);
    const angleOfCircularExtents = [0, 0.5 * Math.PI, 1.0 * Math.PI, 1.5 * Math.PI];
    const angularRange = shape.endAngle > shape.startAngle
        ? shape.endAngle - shape.startAngle
        : shape.endAngle + 2 * Math.PI - shape.startAngle;
    for (const angle of angleOfCircularExtents) {
        if (angle >= shape.startAngle && angle <= shape.startAngle + angularRange) {
            const x = shape.centerX + Math.cos(angle) * shape.radius;
            const y = shape.centerY + Math.sin(angle) * shape.radius;
            maxX = Math.max(maxX, x);
            maxY = Math.max(maxY, y);
            minX = Math.min(minX, x);
            minY = Math.min(minY, y);
        }
    }
    return {
        minX,
        minY,
        maxX,
        maxY,
    };
}
function getVertexAdjacentTrace(traceUid, traceVertex, pcbNodes, pcbTracingGraph) {
    const uid = traceUid;
    const uidVertex = `${uid}_${traceVertex}`;
    if (!pcbTracingGraph.hasNode(uidVertex)) {
        return null;
    }
    const uidConnectedTraceVertices = pcbTracingGraph.neighbors(uidVertex).filter((uidVertex) => {
        if (uidVertex.match(new RegExp(`^${uid}_`, "gi"))) {
            // The same trace
            return false;
        }
        if (!uidVertex.match(/(_start|_end)$/gi)) {
            // Something that's not another adjacent trace
            return false;
        }
        return true;
    });
    if (uidConnectedTraceVertices.length > 1) {
        // too many route connections to start so prevent curve shape from that
        return null;
    }
    const uidConnectedTraceVertex = uidConnectedTraceVertices.at(0);
    if (!uidConnectedTraceVertex) {
        // not expected - returning for cleaner typescript code
        return null;
    }
    const uidAdjacentTrace = uidConnectedTraceVertex.replace(/(_start|_end)$/gi, "") ?? "";
    const nodeAdjacentTrace = pcbNodes[uidAdjacentTrace];
    if (nodeAdjacentTrace?.type !== PcbNodeTypes.routeSegment) {
        // not expected - returning for cleaner typescript code, slight possibility of incorrect graph ids that could cause this
        return null;
    }
    if (uidConnectedTraceVertex.match(/(_start)$/gi)) {
        return { trace: nodeAdjacentTrace, vertex: "start" };
    }
    //
    if (uidConnectedTraceVertex.match(/(_end)$/gi)) {
        return { trace: nodeAdjacentTrace, vertex: "end" };
    }
    return null;
}
function shouldApplyCurve(vertex, traceBakedRules) {
    const vertexTraceShape = vertex === "start" ? "traceShapeStart" : "traceShapeEnd";
    return traceBakedRules[vertexTraceShape] === TraceShapeType.curve;
}
function getTraceVector(bakedRules, from) {
    if (from === "start") {
        return vec2.sub(bakedRules.endPosition, bakedRules.startPosition);
    }
    else {
        return vec2.sub(bakedRules.startPosition, bakedRules.endPosition);
    }
}
function isCornerAppliedToVertex(vertexUid, traceBakedRules) {
    if (vertexUid.match(/(_start)$/gi)) {
        if (shouldApplyCurve("start", traceBakedRules)) {
            return true;
        }
    }
    if (vertexUid.match(/(_end)$/gi)) {
        if (shouldApplyCurve("end", traceBakedRules)) {
            return true;
        }
    }
    return false;
}
function* connectedToCornerTracesGenerator(pcbNodes, graph) {
    if (!graph) {
        return;
    }
    for (const uid in pcbNodes) {
        for (const routeSegment of connectedToCornerTraceGenerator(uid, pcbNodes, graph)) {
            yield routeSegment;
        }
    }
}
function* connectedToCornerTraceGenerator(uid, pcbNodes, graph) {
    {
        if (!graph) {
            return;
        }
        const pcbNode = pcbNodes[uid];
        if (!pcbNode) {
            return;
        }
        if (pcbNode.type !== PcbNodeTypes.routeSegment) {
            return;
        }
        const isCornerOnRouteStart = isCornerAppliedToVertex(`${uid}_start`, pcbNode.bakedRules);
        const isCornerOnRouteEnd = isCornerAppliedToVertex(`${uid}_end`, pcbNode.bakedRules);
        // Check for route segment(s) connected to start vertex
        if (graph.hasNode(`${pcbNode.uid}_start`)) {
            // Iterate route segment(s) connected to start vertex
            for (const uidNeighborVertex of graph.neighbors(`${pcbNode.uid}_start`)) {
                const uidNeighborNode = uidNeighborVertex.replace(/(_start|_end)$/gi, "") ?? "";
                const nodeNeighbor = pcbNodes[uidNeighborNode];
                // Filter only route segments connected to start vertex
                if (nodeNeighbor?.type !== PcbNodeTypes.routeSegment) {
                    continue;
                }
                // Yield route segment that is paired in a a curve
                if (isCornerOnRouteStart ||
                    isCornerAppliedToVertex(uidNeighborVertex, pcbNode.bakedRules)) {
                    yield nodeNeighbor;
                }
            }
        }
        // Check for route segment(s) connected to end vertex
        if (graph.hasNode(`${pcbNode.uid}_end`)) {
            // Iterate route segment(s) connected to end vertex
            for (const uidNeighborVertex of graph.neighbors(`${pcbNode.uid}_end`)) {
                const uidNeighborNode = uidNeighborVertex.replace(/(_start|_end)$/gi, "") ?? "";
                const nodeNeighbor = pcbNodes[uidNeighborNode];
                // Filter only route segments connected to end vertex
                if (nodeNeighbor?.type !== PcbNodeTypes.routeSegment) {
                    continue;
                }
                // Yield route segment that is paired in a a curve
                if (isCornerOnRouteEnd || isCornerAppliedToVertex(uidNeighborVertex, pcbNode.bakedRules)) {
                    yield nodeNeighbor;
                }
            }
        }
    }
}
export function resolveRadius(traceA, traceB) {
    // TODO: check if this is still nessisary
    const safeParse = (value, fallback) => {
        const number = Number(value);
        return Number.isNaN(number) ? fallback : number;
    };
    // For reference: https://coda.io/d/_dV7fDHbd8cS/Flex-PCB-v0-curved-traces_suyeK2yb#_luZCxQTn
    const cornerRadiusMin = Math.max(safeParse(traceA.trace.bakedRules.traceCornerRadiusMinimum, 0), safeParse(traceB.trace.bakedRules.traceCornerRadiusMinimum, 0));
    const cornerRadiusTraceVertexA = traceA.vertex === "start"
        ? traceA.trace.bakedRules.traceCornerRadiusStart
        : traceA.trace.bakedRules.traceCornerRadiusEnd;
    const cornerRadiusTraceVertexB = traceB.vertex === "start"
        ? traceB.trace.bakedRules.traceCornerRadiusStart
        : traceB.trace.bakedRules.traceCornerRadiusEnd;
    // For reference: https://coda.io/d/_dV7fDHbd8cS/Flex-PCB-v0-curved-traces_suyeK2yb#_luEBvqT7
    const cornerRadius = Math.max(safeParse(cornerRadiusTraceVertexA, 0), safeParse(cornerRadiusTraceVertexB, 0));
    const radius = Math.max(0, cornerRadiusMin, cornerRadius);
    return radius;
}
function traceLength(bakedRules) {
    return vec2.distance(bakedRules.startPosition, bakedRules.endPosition);
}
function getDistByRadius(targetRadius, unitTrace, unitBetween) {
    const thetaHalf = vec2.radiansBetween(unitTrace, unitBetween);
    if (Number.isNaN(thetaHalf)) {
        return;
    }
    const tanThetaHalf = Math.tan(thetaHalf);
    if (tanThetaHalf === 0) {
        return;
    }
    const targetProjection = targetRadius / tanThetaHalf;
    return targetProjection;
}
function calcFullAngle(a) {
    let angle = Math.atan2(a.y, a.x);
    if (angle < 0) {
        angle += 2 * Math.PI;
    }
    return angle;
}
function orderedArcAngles(pointJoint, arcCenter, arcStart) {
    const vecToArcStart = new Vector3(arcStart.x - arcCenter.x, arcStart.y - arcCenter.y, 0);
    const vecToPointJoint = new Vector3(pointJoint.x - arcCenter.x, pointJoint.y - arcCenter.y, 0);
    const thetaStart = calcFullAngle({ x: vecToArcStart.x, y: vecToArcStart.y });
    const thetaBetween = vec2.radiansBetween(vecToArcStart, vecToPointJoint);
    if (Math.sign(vecToArcStart.cross(vecToPointJoint).z) > 0) {
        return { from: thetaStart, to: thetaStart + thetaBetween, clockwise: false };
    }
    else {
        return { from: thetaStart - thetaBetween, to: thetaStart, clockwise: true };
    }
}
function segmentByIndex(index, segments, arcPrimitive) {
    const radius = arcPrimitive.radius;
    const thetaRange = arcPrimitive.endAngle - arcPrimitive.startAngle;
    const thetaStep = thetaRange / segments;
    const segmentThetaStart = arcPrimitive.startAngle + thetaStep * (index + 0);
    const segmentThetaEnd = arcPrimitive.startAngle + thetaStep * (index + 1);
    return {
        start: {
            x: arcPrimitive.centerX + Math.cos(segmentThetaStart) * radius,
            y: arcPrimitive.centerY + Math.sin(segmentThetaStart) * radius,
        },
        end: {
            x: arcPrimitive.centerX + Math.cos(segmentThetaEnd) * radius,
            y: arcPrimitive.centerY + Math.sin(segmentThetaEnd) * radius,
        },
    };
}
/**
 * Generates segments that approximate the arc primitive
 *
 * @param arcPrimitive The arc primitive to generate segments for
 * @param params Optional parameters to control the generation of segments. If no params are provided, the default segment length is 0.6mm is used.
 */
function* segmentGenerator(arcPrimitive, params = { segmentLength: SegmentLength }) {
    if (!arcPrimitive) {
        return;
    }
    let segmentCount = 0;
    // If segmentCount is explicity provided, use that to control arc segmentation count
    if ("segmentCount" in params) {
        segmentCount = Math.floor(params.segmentCount);
    }
    // Otherwise, a segment length is provided. This is the dynamic segmentation case - segments
    // are generated to accommodate both the segmentLength and arc length
    else {
        if (params.segmentLength <= 0) {
            return;
        }
        const arcRange = arcPrimitive.endAngle - arcPrimitive.startAngle;
        if (arcRange <= 0) {
            return;
        }
        const arcFrac = arcRange / (2 * Math.PI);
        const arcCircumference = 2 * Math.PI * arcPrimitive.radius * arcFrac;
        segmentCount = Math.ceil(arcCircumference / params.segmentLength);
    }
    if (segmentCount <= 0) {
        return;
    }
    for (let i = 0; i < segmentCount; i++) {
        yield segmentByIndex(i, segmentCount, arcPrimitive);
    }
}
function isVertexConnectedToTraceJunction(uid, vertex, graph) {
    const node = `${uid}_${vertex}`;
    // Checking node exists prevents possibility of runtime exception in subsequent graph queries
    if (!graph.hasNode(node)) {
        return false;
    }
    const nodeNeighbors = graph.neighbors(node);
    // Count the number of neighbors to this node, that are route vertices. This function is trying
    // to determine if a junction of trace are connected to this route vertex (ie 3 or more route
    // segments are connected to this vertex). We therefore filter out non-route vertex neighbors
    const traceNeighbors = Arrays.count(nodeNeighbors, (nodeNeighbor) => {
        // This is a fast way to check if a node is a route vertex, without
        // requiring a PCB node map lookup, and node type check comparison.
        // Node for route vertices are unique; they have a _start or _end
        // suffix. I take advantage of that to count neighbor nodes that are
        // routes, by checking for suffix.
        const isNodeRouteVertex = !!nodeNeighbor.match(/(_start|_end)$/gi);
        return isNodeRouteVertex;
    });
    return traceNeighbors > 2;
}
function isVertexConnectedToPadOrVia(traceVertex, traceUid, pcbNodes, pcbTracingGraph) {
    const nodeUid = `${traceUid}_${traceVertex}`;
    if (pcbTracingGraph.hasNode(nodeUid)) {
        for (const uid of pcbTracingGraph.neighbors(nodeUid)) {
            const pcbNode = pcbNodes[uid];
            switch (pcbNode?.type) {
                case PcbNodeTypes.pad:
                case PcbNodeTypes.via:
                case PcbNodeTypes.smartVia:
                    return true;
            }
        }
    }
    return false;
}
function computeForVertex(traceVertex, traceProps, pcbNodes, pcbTracingGraph) {
    if (!pcbTracingGraph) {
        // This is sometimes not present ... ?!??! race conditions ?!??!
        return;
    }
    // If a trace vertex is connected to a pad or via, we don't want to apply a curve it. For example,
    // if two traces are connected by a vertex that is positioned atop a Pad, then a curve should not be
    // applied. Why? A curve would cause the trace to detach from the Pad, which will not be what the user
    // wants
    if (isVertexConnectedToPadOrVia(traceVertex, traceProps.uid, pcbNodes, pcbTracingGraph)) {
        return;
    }
    const adjacentOnVertex = getVertexAdjacentTrace(traceProps.uid, traceVertex, pcbNodes, pcbTracingGraph);
    if (!adjacentOnVertex) {
        return;
    }
    // Only allow curve if either trace around vertex has curve shape selected
    if (!(shouldApplyCurve(traceVertex, traceProps.bakedRules) ||
        shouldApplyCurve(adjacentOnVertex.vertex, adjacentOnVertex.trace.bakedRules))) {
        return;
    }
    const vectorTrace = getTraceVector(traceProps.bakedRules, traceVertex);
    const vectorOther = getTraceVector(adjacentOnVertex.trace.bakedRules, adjacentOnVertex.vertex);
    const pointJoint = traceVertex === "start" ? traceProps.bakedRules.startPosition : traceProps.bakedRules.endPosition;
    const targetRadius = resolveRadius({
        trace: traceProps,
        vertex: traceVertex,
    }, adjacentOnVertex);
    const unitTrace = vec2.normalize(vectorTrace) ?? { x: 0, y: 0 };
    const unitOther = vec2.normalize(vectorOther) ?? { x: 0, y: 0 };
    // This deals with a corner case where, two traces that are essentially parallel with one
    // another, and are directed towards one another (ie their dot product is basically -1.0)
    // results in attempted curve fitting between those traces producing broken curve geometry.
    // The broken geometry is basically an inverted curve being drawn back to from between inflexed
    // start and end points of the curve.
    // We therefore avoid attempting to fitting a curve in this case, since the traces are
    // practically parallel anyway by returning undefined
    {
        const unitScalar = vec2.dot(unitTrace, unitOther);
        const unitScalarAbs = Math.abs(unitScalar);
        if (isCloseTo(unitScalarAbs, 1, ToleranceCornerRadius /* Reuse this as a floating point tolerance */)) {
            return undefined;
        }
    }
    const unitBetween = vec2.normalize(vec2.add(unitTrace, unitOther)) ?? { x: 0, y: 0 };
    const lengthTrace = traceLength(traceProps.bakedRules);
    const lengthOther = traceLength(adjacentOnVertex.trace.bakedRules);
    const distTargetRadius = getDistByRadius(targetRadius, unitTrace, unitBetween);
    if (distTargetRadius === undefined) {
        return;
    }
    const shouldMaximizeArcRadius = (traceUid) => {
        if (isVertexConnectedToPadOrVia("start", traceUid, pcbNodes, pcbTracingGraph)) {
            return true;
        }
        if (isVertexConnectedToPadOrVia("end", traceUid, pcbNodes, pcbTracingGraph)) {
            return true;
        }
        if (isVertexConnectedToTraceJunction(traceUid, "start", pcbTracingGraph)) {
            return true;
        }
        if (isVertexConnectedToTraceJunction(traceUid, "end", pcbTracingGraph)) {
            return true;
        }
        return false;
    };
    /*
    Idea: preserve full curve when curve vertex is adjacent to a junction vertex

    This can be achieved by denominating the clamp trace distance by 1, rather than 2.

    The denominator of two clamps the starting point of a curve, on a trace, to a maximum of
    half the trace's length. A denominator of one allows the curve to start at any position
    along the trace, allowing the original curve size to be retained

    See this diagram for a visual explanation: https://www.figma.com/board/T8mULZFAwdVvrOQApF3AAc/Untitled?node-id=1-178&t=BdRVX9QxKtCm6j08-4
    */
    const clampLengthTrace = lengthTrace / (shouldMaximizeArcRadius(traceProps.uid) ? 1 : 2);
    const clampLengthOther = lengthOther / (shouldMaximizeArcRadius(adjacentOnVertex.trace.uid) ? 1 : 2);
    const distClampTraces = Math.min(clampLengthTrace, clampLengthOther);
    const distClamp = Math.min(distTargetRadius, distClampTraces);
    const distMidline = distClamp / Math.cos(vec2.radiansBetween(unitTrace, unitBetween));
    const center = vec2.add(pointJoint, vec2.scale(unitBetween, distMidline));
    const start = vec2.add(pointJoint, vec2.scale(unitTrace, distClamp));
    const radius = vec2.distance(center, start);
    if (radius <= 0) {
        return;
    }
    const { from: startAngle, to: endAngle, clockwise } = orderedArcAngles(pointJoint, center, start);
    return {
        vertex: new Vector3(start.x, start.y, 0),
        arc: {
            centerX: center.x,
            centerY: center.y,
            radius,
            endAngle,
            startAngle,
            clockwise,
        },
    };
}
// TODO: dedupe this logic with computeForVertex(..)
function computeRadius(traceVertex, traceProps, pcbNodes, pcbTracingGraph) {
    if (!pcbTracingGraph) {
        return;
    }
    const adjacentOnVertex = getVertexAdjacentTrace(traceProps.uid, traceVertex, pcbNodes, pcbTracingGraph);
    if (!adjacentOnVertex) {
        return;
    }
    // Only allow curve if either trace around vertex has curve shape selected
    if (!(shouldApplyCurve(traceVertex, traceProps.bakedRules) ||
        shouldApplyCurve(adjacentOnVertex.vertex, adjacentOnVertex.trace.bakedRules))) {
        return;
    }
    const vectorTrace = getTraceVector(traceProps.bakedRules, traceVertex);
    const vectorOther = getTraceVector(adjacentOnVertex.trace.bakedRules, adjacentOnVertex.vertex);
    const pointJoint = traceVertex === "start" ? traceProps.bakedRules.startPosition : traceProps.bakedRules.endPosition;
    const targetRadius = resolveRadius({
        trace: traceProps,
        vertex: traceVertex,
    }, adjacentOnVertex);
    const unitTrace = vec2.normalize(vectorTrace) ?? { x: 0, y: 0 };
    const unitOther = vec2.normalize(vectorOther) ?? { x: 0, y: 0 };
    const unitBetween = vec2.normalize(vec2.add(unitTrace, unitOther)) ?? { x: 0, y: 0 };
    const lengthTrace = traceLength(traceProps.bakedRules);
    const lengthOther = traceLength(adjacentOnVertex.trace.bakedRules);
    const distTargetRadius = getDistByRadius(targetRadius, unitTrace, unitBetween);
    if (distTargetRadius === undefined) {
        return;
    }
    const distClampTraces = Math.min(lengthTrace, lengthOther) / 2;
    const distClamp = Math.min(distTargetRadius, distClampTraces);
    const distMidline = distClamp / Math.cos(vec2.radiansBetween(unitTrace, unitBetween));
    const center = vec2.add(pointJoint, vec2.scale(unitBetween, distMidline));
    const start = vec2.add(pointJoint, vec2.scale(unitTrace, distClamp));
    const radius = vec2.distance(center, start);
    return radius;
}
function computeArcLength(arcPrimitive) {
    // Calculate the circumference of the arc circle
    const circumference = 2 * Math.PI * arcPrimitive.radius;
    // Calculate the angular range of the arc and the fraction of the
    // full circle it represents
    const thetaRange = arcPrimitive.endAngle - arcPrimitive.startAngle;
    const thetaFrac = thetaRange / (2 * Math.PI);
    // Return the length of the arc curve as a fraction of the whole
    // arc circle
    return circumference * thetaFrac;
}
function computeLength(traceProps, pcbNodes, pcbTracingGraph) {
    const arcStart = computeForVertex("start", traceProps, pcbNodes, pcbTracingGraph);
    const arcEnd = computeForVertex("end", traceProps, pcbNodes, pcbTracingGraph);
    const lineStart = arcStart?.vertex ?? traceProps.bakedRules.startPosition;
    const lineEnd = arcEnd?.vertex ?? traceProps.bakedRules.endPosition;
    const lineLength = vec2.distance(lineStart, lineEnd);
    const arcLengthStart = arcStart ? computeArcLength(arcStart.arc) : 0;
    const arcLengthEnd = arcEnd ? computeArcLength(arcEnd.arc) : 0;
    return arcLengthStart + lineLength + arcLengthEnd;
}
function isCornerTraceRule(rule) {
    switch (rule.key) {
        case "traceCornerRadius":
        case "traceCornerRadiusStart":
        case "traceCornerRadiusEnd":
        case "traceCornerRadiusMinimum":
        case "traceShape":
        case "traceShapeStart":
        case "traceShapeEnd":
            return true;
        default:
            return false;
    }
}
export const TraceCorners = {
    isCornerTraceRule,
    segmentByIndex,
    segmentGenerator,
    computeForVertex,
    computeRadius,
    computeLength,
    shapeBounds,
    connectedToCornerTracesGenerator,
    connectedToCornerTraceGenerator,
};
//# sourceMappingURL=traceCorners.js.map